import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";

interface GoogleSignInButtonProps {
  onSuccess: (token: string) => void;
  disabled?: boolean;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onSuccess, disabled }) => {
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = () => {
    if (disabled || googleLoading) return;
    setGoogleLoading(true);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId || clientId === "your-google-client-id-here") {
      console.error(
        "Google Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in .env file"
      );
      alert(
        "Google Sign-In is not configured. Please contact the administrator."
      );
      setGoogleLoading(false);
      return;
    }

    // Initialize Google Identity Services with popup mode
    if (window.google) {
      window.google.accounts.oauth2
        .initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: (response: any) => {
            if (response.error) {
              console.error("Google OAuth error:", response.error);
              setGoogleLoading(false);
              return;
            }

            // Get user info from the access token
            fetch(
              `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`
            )
              .then((res) => res.json())
              .then((userInfo) => {
                // Create a mock credential object similar to One Tap
                const credential = {
                  credential: btoa(
                    JSON.stringify({
                      sub: userInfo.id,
                      email: userInfo.email,
                      name: userInfo.name,
                      picture: userInfo.picture,
                      given_name: userInfo.given_name,
                      family_name: userInfo.family_name,
                    })
                  ),
                };
                onSuccess(credential.credential);
                setGoogleLoading(false);
              })
              .catch((error) => {
                console.error("Error fetching user info:", error);
                alert("Failed to get user information from Google");
                setGoogleLoading(false);
              });
          },
        })
        .requestAccessToken();
    } else {
      console.error("Google Identity Services not loaded");
      alert("Google Sign-In service is not available. Please try again later.");
      setGoogleLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={handleGoogleSignIn}
      disabled={disabled || googleLoading}
    >
      {googleLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Signing in...
        </>
      ) : (
        <>
          <svg
            className="mr-2 h-4 w-4"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </>
      )}
    </Button>
  );
};

export default GoogleSignInButton;
